import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import React from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import { showMedicalContentSelector } from '../../../redux/config/selfData';
import { OutlineButton } from '../../../components/outlineButton';
import { PanelContainer } from '../../../components/panel';
import { IGetResidentRes } from '../../../types/api/resident/getResident';
import { useFetchResidents } from '../../../hooks/api/resident/useFetchResidents';
import { useFetchDailyCareRecord } from '../../../hooks/api/traversal/useFetchDailyCareRecord';
import { ResidentsTable } from '../residentsTable';
import { useCareRecordCsvExporter } from '../../../hooks/useCareRecordCsvExporter';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

interface IDisplayResident extends IGetResidentRes {
  isChecked: boolean; // テーブルのチェック状態の管理フラグ
}

const TopContainer = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
`;

const DateRangePicker = css`
  display: flex;
  align-items: center;
  gap: 24px;
`;

export const CareRecordCSV = React.memo(() => {
  const rangeMinDate = dayjs().startOf('day').subtract(3, 'year');
  const rangeMaxDate = dayjs().endOf('day');
  const { residents } = useFetchResidents(true);
  const { fetchDailyCareRecord } = useFetchDailyCareRecord(false);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [tableData, setTableData] = React.useState<IDisplayResident[]>();
  const [checkArray, setCheckArray] = React.useState<boolean[]>();
  const [onSend, setOnSend] = React.useState<boolean>(false);
  const [fetchTimestamp, setFetchTimestamp] = React.useState<{ start: Dayjs; end: Dayjs }>({
    start: dayjs().startOf('day').subtract(6, 'day'),
    end: dayjs().endOf('day'),
  });
  const { exportCareRecordCsv } = useCareRecordCsvExporter();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const pageTitle =
    t('csvdownload', 'CSVダウンロード') +
    ' - ' +
    (showMedicalContent ? t('urinationDiary', '排尿日誌') : t('careHistory', 'ケア履歴'));

  const selectedResidents: IGetResidentRes[] = React.useMemo(() => {
    if (!residents || !checkArray) return [];
    return residents?.filter((v, i) => {
      return checkArray[i] === true;
    });
  }, [checkArray, residents]);

  const handleChangeStartTime = (start: Dayjs | null) => {
    if (!start) return;
    setFetchTimestamp((prev) => {
      return { ...prev, start: start.startOf('day') };
    });
  };

  const handleChangeEndTime = (end: Dayjs | null) => {
    if (!end) return;
    setFetchTimestamp((prev) => {
      return { ...prev, end: end.endOf('day') };
    });
  };

  const handleClickCSVDownload = async () => {
    if (!selectedResidents.length) {
      showSnackbar(t('noResidentSelected', '入居者が選択されていません'), 'error');
      return;
    }
    if (!fetchTimestamp?.start || !fetchTimestamp?.end) {
      return;
    }
    setOnSend(true);
    try {
      const records = await Promise.all(
        selectedResidents.map(async (v) => {
          return await fetchDailyCareRecord(
            v.id,
            formatUtcFetchTimestamp(fetchTimestamp.start),
            formatUtcFetchTimestamp(fetchTimestamp.end),
          );
        }),
      );
      const exported = await exportCareRecordCsv({
        data: records,
        start: fetchTimestamp.start,
        end: fetchTimestamp.end,
      });
      if (!exported) {
        showSnackbar(t('dataForTheSpecifiedPeriodWasNotFound', '指定した期間のデータが見つかりませんでした'));
      }
    } finally {
      setOnSend(false);
    }
  };

  const handleChangeCheckBox = (checkArray: boolean[]) => {
    // TODO: selectedResidentsの更新検知のためにスプレッド演算子で更新を行なっているが
    // チェックボックスの状態を変更すると描画がTable全体に入るため要見直し
    setCheckArray([...checkArray]);
  };

  const createTableData = (data: IGetResidentRes[] | undefined) => {
    return data?.map((v) => {
      return {
        ...v,
        isChecked: false,
      };
    });
  };

  React.useEffect(() => {
    if (!residents) return;
    const tableData = createTableData(residents);
    setTableData(tableData);
  }, [residents]);

  return (
    <PanelContainer title={pageTitle}>
      {tableData && tableData.length ? (
        <>
          <div css={TopContainer}>
            <div css={DateRangePicker}>
              <DatePicker
                views={['year', 'month', 'day']}
                label={t('startDate', '開始日')}
                value={fetchTimestamp?.start}
                minDate={rangeMinDate}
                maxDate={fetchTimestamp?.end ?? undefined}
                onChange={handleChangeStartTime}
                renderInput={(params) => <TextField variant='standard' {...params} helperText={null} />}
              />
              〜
              <DatePicker
                views={['year', 'month', 'day']}
                label={t('endDate', '終了日')}
                value={fetchTimestamp?.end}
                minDate={fetchTimestamp?.start ?? undefined}
                maxDate={rangeMaxDate}
                onChange={handleChangeEndTime}
                renderInput={(params) => <TextField variant='standard' {...params} helperText={null} />}
              />
            </div>
            <Tooltip
              placement='top-end'
              title={t(
                'downloadingMayTakeSomeTimeDependingOnTheAmountOfData',
                'データ量に応じてダウンロードに時間がかかる場合がございます。'
              )}
            >
              <div>
                <OutlineButton
                  icon={<img src='/images/analysis/icon_dl_csv.svg' alt='CSV' />}
                  onClick={handleClickCSVDownload}
                  disable={onSend}
                >
                  {t('download', 'ダウンロード')}
                </OutlineButton>
              </div>
            </Tooltip>
          </div>

          <ResidentsTable data={tableData} onChange={handleChangeCheckBox} />
        </>
      ) : (
        <>{t('thereAreNoResidents', '入居者がいません')}</>
      )}
    </PanelContainer>
  );
});
